/**
 * Canonical FAQPage schema page resolution.
 *
 * Google's structured-data rules require each FAQ Q&A to be marked up exactly
 * once across the site (https://developers.google.com/search/docs/appearance/structured-data/faqpage).
 * Visible HTML can repeat as much as we want; the JSON-LD <script> can not.
 *
 * We surface the same CMS-managed FAQs in three places:
 *
 *   1. Landing pages tagged via the FAQ's `pages` array (e.g. `/how-it-works`,
 *      `/insurance`) — supplementary FAQ sections that double as topical context.
 *   2. The dedicated `/faq/<slug>` page — one URL per question.
 *   3. The `/faq` index — directory listing of every FAQ.
 *
 * Only one of those surfaces should emit the FAQPage schema for any given
 * question. For any CMS FAQ that has a `slug`, the canonical home is the
 * dedicated `/faq/<slug>` page — that's the URL whose entire purpose is to
 * answer this one question, so it's the correct target for both Google's
 * rich-results crawler and AI-Overview citation. Landing pages (including
 * any that the FAQ's `pages` array points at) still render the question
 * visibly in their FAQ section, but defer the JSON-LD to `/faq/<slug>` to
 * stay within Google's single-markup rule.
 *
 * Local hardcoded FAQs (no `slug`, no `pages`) live only on the page that
 * renders them, so they're always canonical there.
 *
 * The `/faq` index never qualifies as canonical — it's a directory, not a
 * topical page, and putting schema there gives AI Overviews / rich-results
 * markup the wrong landing target. Index pages should pass `currentPage="faq"`
 * (or any value not present in CMS `pages` options) and `mainEntityFor` will
 * naturally return an empty array.
 *
 * History: an earlier iteration of this resolver picked `pages[0]` over
 * `faq/<slug>` so editorial could route schema to higher-converting landing
 * pages. In practice that left all 184 CMS FAQ detail pages without any
 * FAQPage schema (flagged by SEO audit in AHD-6311), since most FAQs are
 * tagged to a landing page. The dedicated detail page is now the single
 * canonical home — it's the URL that exactly matches one Question, which
 * is what FAQPage schema is designed for.
 */

export interface FaqCanonicalInput {
  /** CMS slug. Optional because some pages render hardcoded local FAQs. */
  slug?: string
  /** CMS `pages` field. Optional / nullable for local FAQs. */
  pages?: string[] | null
}

/**
 * Whether `faq` is canonical to `currentPage` for FAQPage schema purposes.
 *
 * The decision tree, in order:
 *
 *   0. **CMS FAQ whose first `pages` tag is `about`:** the /about company FAQ
 *      has its schema home on /about (it is hidden from the /faq index and
 *      search, so its detail page is not a surface of its own) — emit only
 *      when `currentPage === "about"`.
 *   1. **CMS FAQ with a `slug`:** canonical lives at `/faq/<slug>` regardless
 *      of the `pages` array — emit only when `currentPage === "faq/<slug>"`.
 *      The `pages` array still drives where the question is *displayed* (via
 *      the FAQ section on those landing pages), it just no longer affects
 *      where the JSON-LD goes.
 *   2. **Local hardcoded FAQ (no `slug`):** the Q&A only exists on this page,
 *      so it's always canonical here. Schema is safe to emit.
 *
 * Returning `false` means: render the FAQ visibly, but skip it in the
 * mainEntity array — another page (or none) owns the schema.
 */
export function isCanonicalForPage(
  faq: FaqCanonicalInput,
  currentPage: string
): boolean {
  if (faq.pages?.[0] === "about") return currentPage === "about"
  if (faq.slug) return currentPage === `faq/${faq.slug}`
  return true
}

/**
 * Subset of `faqs` whose canonical schema page is `currentPage`. Use this to
 * derive the `mainEntity` array for `<JsonLd data={createFAQPageSchema(...)} />`.
 *
 * The visible accordion still renders all FAQs — only the schema is filtered.
 */
export function mainEntityFor<T extends FaqCanonicalInput>(
  faqs: T[],
  currentPage: string
): T[] {
  return faqs.filter((f) => isCanonicalForPage(f, currentPage))
}
